/**
 * CommandPalette — Cmd-K / Ctrl-K quick navigation across every page in the app.
 *
 * Opens via keyboard shortcut OR the "Quick Search" button at the top of the
 * sidebar. Fuzzy-filters all pages (including grouped + utility items) and
 * navigates on Enter / click. ESC closes.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from "@/components/ui/command";
import {
  LayoutDashboard, Inbox as InboxIcon, Bell, Map as MapIcon, Users,
  KanbanSquare, UserPlus, Calculator, LineChart, PiggyBank, FileSignature,
  UserSquare, Mail, CheckSquare, DollarSign, BarChart3, Trophy, Flame, Gift,
  Settings, CreditCard, ShieldCheck, MapPin, UserCircle, FileText, Sparkles, Pin,
  BadgeDollarSign, Zap,
} from "lucide-react";

const RESULT_ICON = {
  lead: MapPin,
  team: UserCircle,
  envelope: FileText,
};

const PAGES = {
  Essentials: [
    { to: "/v3", label: "Dashboard", icon: LayoutDashboard, end: true,
      keywords: "home overview stats" },
    { to: "/v3/crm/inbox", label: "Inbox", icon: InboxIcon,
      keywords: "deal of the day verdict ai" },
    { to: "/v3/alerts", label: "Alerts", icon: Bell,
      keywords: "saved searches notifications new deals" },
  ],
  "Deal Flow": [
    { to: "/v3/crm/discover", label: "Discover", icon: MapIcon,
      keywords: "search map properties rentcast find" },
    { to: "/v3/crm/leads", label: "Leads", icon: Users,
      keywords: "crm contacts sellers" },
    { to: "/v3/crm/pipeline", label: "Pipeline", icon: KanbanSquare,
      keywords: "kanban stages drag" },
    { to: "/v3/crm/outreach/captures", label: "Captures", icon: UserPlus,
      keywords: "buyer seller capture form public report" },
  ],
  Underwriting: [
    { to: "/v3/tools/analyzer", label: "Analyzer", icon: Calculator,
      keywords: "70 rule MAO flip BRRRR rental novation wholetail sub-to calculate verdict" },
    { to: "/v3/markets", label: "Markets", icon: LineChart,
      keywords: "metros median DOM rent growth inventory" },
    { to: "/v3/tools/portfolio", label: "Portfolio", icon: PiggyBank,
      keywords: "owned properties cash flow equity cap rate" },
    { to: "/v3/crm/contracts", label: "Contracts", icon: FileSignature,
      keywords: "docusign envelopes assignment purchase JV e-signature" },
  ],
  Outreach: [
    { to: "/v3/business/team", label: "Power Team", icon: UserSquare,
      keywords: "buyers contractors lenders rolodex" },
    { to: "/v3/crm/outreach/drips", label: "Drips", icon: Mail,
      keywords: "email campaigns nurture automations" },
    { to: "/v3/crm/outreach/sequences", label: "Sequences", icon: Zap,
      keywords: "cadence multi-channel sms email task speed contact blitz automation" },
    { to: "/v3/tools/tasks", label: "Tasks", icon: CheckSquare,
      keywords: "todo follow up reminders" },
    { to: "/v3/crm/outreach/marketing", label: "Marketing", icon: DollarSign,
      keywords: "spend roi attribution channels" },
  ],
  Growth: [
    { to: "/v3/business/analytics", label: "Analytics", icon: BarChart3,
      keywords: "metrics charts performance" },
    { to: "/v3/growth/wins", label: "Wins", icon: Trophy,
      keywords: "brag wall closed deals" },
    { to: "/v3/growth/leaderboard", label: "Leaderboard", icon: Flame,
      keywords: "ranking competition streaks" },
    { to: "/v3/growth/referrals", label: "Refer & Earn", icon: Gift,
      keywords: "share invite friends bonus free month" },
    { to: "/v3/growth/affiliate", label: "Affiliate Program", icon: BadgeDollarSign,
      keywords: "earn commission 30 percent recurring 12 months payout stripe connect" },
  ],
  Account: [
    { to: "/v3/settings", label: "Settings", icon: Settings,
      keywords: "profile brand logo accent" },
    { to: "/v3/business/billing", label: "Billing", icon: CreditCard,
      keywords: "subscription tier plan stripe upgrade" },
  ],
};

export default function CommandPalette({ open, onOpenChange, isAdmin }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [dataResults, setDataResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [aiResults, setAiResults] = useState([]);
  const [aiExplain, setAiExplain] = useState("");
  const [aiThinking, setAiThinking] = useState(false);
  const [aiSpec, setAiSpec] = useState(null);
  const [pinning, setPinning] = useState(false);
  const [pinnedJustNow, setPinnedJustNow] = useState(false);

  // Cmd/Ctrl-K toggle.
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        onOpenChange(!open);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onOpenChange]);

  // Reset query whenever the modal closes so the next open starts clean.
  const handleOpenChange = (next) => {
    if (!next) {
      setQuery("");
      setDataResults([]);
      setAiResults([]);
      setAiExplain("");
      setAiSpec(null);
      setPinnedJustNow(false);
    }
    onOpenChange(next);
  };

  const runAi = async () => {
    const q = query.trim();
    if (q.length < 3 || aiThinking) return;
    setAiThinking(true);
    setAiResults([]);
    setAiExplain("");
    setAiSpec(null);
    setPinnedJustNow(false);
    try {
      const { data } = await api.post("/search/semantic", { q });
      setAiResults(data?.results || []);
      setAiExplain(data?.explain || "");
      setAiSpec({
        intent: data?.intent || "leads",
        filters: data?.filters || {},
        explain: data?.explain || "",
      });
    } catch (e) {
      setAiResults([]);
      setAiExplain("AI search is unavailable right now.");
    } finally {
      setAiThinking(false);
    }
  };

  const pinSearch = async () => {
    if (!aiSpec || pinning) return;
    const q = query.trim();
    if (!q) return;
    setPinning(true);
    try {
      await api.post("/smart-searches", {
        name: q.length > 40 ? `${q.slice(0, 37)}…` : q,
        q,
        intent: aiSpec.intent,
        filters: aiSpec.filters,
        explain: aiSpec.explain,
        pinned: true,
      });
      setPinnedJustNow(true);
    } catch (e) {
      // toast handled silently — palette stays open
    } finally {
      setPinning(false);
    }
  };

  // Debounced universal-search call (300ms) — only fires for queries ≥ 2 chars.
  useEffect(() => {
    if (!open) return undefined;
    const q = query.trim();
    if (q.length < 2) {
      setDataResults([]);
      setSearching(false);
      return undefined;
    }
    setSearching(true);
    const ctrl = new AbortController();
    const timer = setTimeout(() => {
      api
        .get(`/search?q=${encodeURIComponent(q)}&limit=12`, { signal: ctrl.signal })
        .then((res) => {
          if (ctrl.signal.aborted) return;
          setDataResults(res.data?.results || []);
          setSearching(false);
        })
        .catch(() => {
          if (ctrl.signal.aborted) return;
          setDataResults([]);
          setSearching(false);
        });
    }, 300);
    return () => {
      ctrl.abort();
      clearTimeout(timer);
    };
  }, [query, open]);

  const grouped = useMemo(() => {
    const out = { lead: [], team: [], envelope: [] };
    for (const r of dataResults) {
      if (out[r.type]) out[r.type].push(r);
    }
    return out;
  }, [dataResults]);

  const go = (to) => {
    onOpenChange(false);
    navigate(to);
  };

  // Lower-cased query for our manual page filter.
  const lq = query.trim().toLowerCase();
  const matchPage = (item) => {
    if (!lq) return true;
    return (
      item.label.toLowerCase().includes(lq) ||
      (item.keywords || "").toLowerCase().includes(lq)
    );
  };

  return (
    <CommandDialog open={open} onOpenChange={handleOpenChange} shouldFilter={false}>
      <CommandInput
        placeholder="Search pages, leads, contacts, envelopes…"
        value={query}
        onValueChange={setQuery}
        data-testid="command-palette-input"
      />
      <CommandList data-testid="command-palette-list">
        <CommandEmpty>
          {searching
            ? "Searching…"
            : "No matches. Try a lead address, a buyer name, or a page like 'analyzer'."}
        </CommandEmpty>

        {/* AI semantic-search trigger — visible whenever the query is long
            enough to be a real question. One click → Claude. */}
        {query.trim().length >= 4 && (
          <CommandGroup heading="Ask AI">
            <CommandItem
              value={query}
              onSelect={runAi}
              data-testid="command-ask-ai"
              forceMount
            >
              <Sparkles className="mr-2 text-emerald-500" />
              <div className="flex flex-col min-w-0">
                <span className="truncate">
                  {aiThinking ? "Thinking…" : `Ask AI: "${query.trim()}"`}
                </span>
                <span className="text-xs opacity-60 truncate">
                  {aiExplain || "Translate plain English into a Mongo query (city, stage, price, stale, etc.)"}
                </span>
              </div>
              <CommandShortcut>AI</CommandShortcut>
            </CommandItem>
            <CommandSeparator />
          </CommandGroup>
        )}

        {/* AI semantic results — rendered above the keyword-match results
            so the user sees Claude's answer first. */}
        {aiResults.length > 0 && (
          <CommandGroup heading="AI Results">
            {aiResults.map((r) => {
              const Icon = RESULT_ICON[r.type] || FileText;
              return (
                <CommandItem
                  key={`ai-${r.type}-${r.id}`}
                  value={`${query} ${r.title}`}
                  onSelect={() => go(r.url)}
                  data-testid={`command-ai-${r.type}-${r.id}`}
                  forceMount
                >
                  <Icon className="mr-2 opacity-70" />
                  <div className="flex flex-col min-w-0">
                    <span className="truncate">{r.title}</span>
                    <span className="text-xs opacity-60 truncate">{r.subtitle}</span>
                  </div>
                  <CommandShortcut>{r.badge}</CommandShortcut>
                </CommandItem>
              );
            })}
            <CommandSeparator />
          </CommandGroup>
        )}

        {/* Pin-to-dashboard CTA — only shown after AI search returned results
            and the user hasn't already pinned this query. */}
        {aiSpec && aiResults.length > 0 && (
          <CommandGroup heading="Pin">
            <CommandItem
              value={`pin save ${query}`}
              onSelect={pinSearch}
              data-testid="command-pin-search"
              disabled={pinnedJustNow}
              forceMount
            >
              <Pin
                className={`mr-2 ${pinnedJustNow ? "text-emerald-600" : "text-amber-500"}`}
              />
              <div className="flex flex-col min-w-0">
                <span className="truncate">
                  {pinnedJustNow
                    ? "Pinned to dashboard"
                    : pinning
                      ? "Pinning…"
                      : "Pin this search to dashboard"}
                </span>
                <span className="text-xs opacity-60 truncate">
                  Live tile showing the count + top 3 matches, auto-refreshed.
                </span>
              </div>
              <CommandShortcut>{pinnedJustNow ? "✓" : "Pin"}</CommandShortcut>
            </CommandItem>
            <CommandSeparator />
          </CommandGroup>
        )}

        {/* Live data results from the universal-search endpoint. Rendered
            first so users see their actual leads / contacts above static pages. */}
        {grouped.lead.length > 0 && (
          <CommandGroup heading="Leads">
            {grouped.lead.map((r) => {
              const Icon = RESULT_ICON.lead;
              return (
                <CommandItem
                  key={`lead-${r.id}`}
                  value={`${r.title} ${r.subtitle}`}
                  onSelect={() => go(r.url)}
                  data-testid={`command-data-lead-${r.id}`}
                >
                  <Icon className="mr-2 opacity-70" />
                  <div className="flex flex-col min-w-0">
                    <span className="truncate">{r.title}</span>
                    <span className="text-xs opacity-60 truncate">{r.subtitle}</span>
                  </div>
                  <CommandShortcut>Lead</CommandShortcut>
                </CommandItem>
              );
            })}
            <CommandSeparator />
          </CommandGroup>
        )}
        {grouped.team.length > 0 && (
          <CommandGroup heading="Power Team">
            {grouped.team.map((r) => {
              const Icon = RESULT_ICON.team;
              return (
                <CommandItem
                  key={`team-${r.id}`}
                  value={`${r.title} ${r.subtitle}`}
                  onSelect={() => go(r.url)}
                  data-testid={`command-data-team-${r.id}`}
                >
                  <Icon className="mr-2 opacity-70" />
                  <div className="flex flex-col min-w-0">
                    <span className="truncate">{r.title}</span>
                    <span className="text-xs opacity-60 truncate">{r.subtitle}</span>
                  </div>
                  <CommandShortcut>{r.badge}</CommandShortcut>
                </CommandItem>
              );
            })}
            <CommandSeparator />
          </CommandGroup>
        )}
        {grouped.envelope.length > 0 && (
          <CommandGroup heading="Envelopes">
            {grouped.envelope.map((r) => {
              const Icon = RESULT_ICON.envelope;
              return (
                <CommandItem
                  key={`env-${r.id}`}
                  value={`${r.title} ${r.subtitle}`}
                  onSelect={() => go(r.url)}
                  data-testid={`command-data-envelope-${r.id}`}
                >
                  <Icon className="mr-2 opacity-70" />
                  <div className="flex flex-col min-w-0">
                    <span className="truncate">{r.title}</span>
                    <span className="text-xs opacity-60 truncate">{r.subtitle}</span>
                  </div>
                  <CommandShortcut>{r.badge}</CommandShortcut>
                </CommandItem>
              );
            })}
            <CommandSeparator />
          </CommandGroup>
        )}

        {Object.entries(PAGES).map(([groupLabel, items], idx) => {
          const filtered = items.filter(matchPage);
          if (filtered.length === 0) return null;
          return (
            <CommandGroup key={groupLabel} heading={groupLabel}>
              {filtered.map((item) => (
                <CommandItem
                  key={item.to}
                  value={item.to}
                  onSelect={() => go(item.to)}
                  data-testid={`command-item-${item.to.replace(/\//g, "-")}`}
                >
                  <item.icon className="mr-2 opacity-70" />
                  <span>{item.label}</span>
                  {idx === 0 && item.to === "/v3" && (
                    <CommandShortcut>Home</CommandShortcut>
                  )}
                </CommandItem>
              ))}
              {idx < Object.keys(PAGES).length - 1 && <CommandSeparator />}
            </CommandGroup>
          );
        })}
        {isAdmin && (
          <CommandGroup heading="Admin">
            <CommandItem
              value="admin users revenue health flags"
              onSelect={() => go("/app/admin")}
              data-testid="command-item-admin"
            >
              <ShieldCheck className="mr-2 opacity-70" />
              <span>Admin Console</span>
            </CommandItem>
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
}
